import React, { useState, useEffect, useCallback } from 'react'
import { 
  Calendar, 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  User, 
  MapPin, 
  Clock,
  ChevronDown,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  X
} from 'lucide-react'
import { leaveService } from '../../../services/leaveService'
import branchService from '../../../services/branchService'
import { leaveTypeService } from '../../../services/leaveService'
import CreateLeaveModal from '../../modals/CreateLeaveModal'
import EditLeaveModal from '../../modals/EditLeaveModal'
import ViewLeaveModal from '../../modals/ViewLeaveModal'
import DeleteLeaveModal from '../../modals/DeleteLeaveModal'

const LeaveApplications = () => {
  const [leaves, setLeaves] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [branchFilter, setBranchFilter] = useState('all')
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('all')

  // Filter data
  const [branches, setBranches] = useState([])
  const [leaveTypes, setLeaveTypes] = useState([])

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedLeave, setSelectedLeave] = useState(null)

  // Toast state
  const [toastMessage, setToastMessage] = useState(null)

  // Toast notification function
  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type })
    setTimeout(() => setToastMessage(null), 5000)
  }

  const fetchLeaves = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {}
      if (statusFilter !== 'all') params.status = statusFilter
      if (branchFilter !== 'all') params.branch = branchFilter
      if (leaveTypeFilter !== 'all') params.leaveType = leaveTypeFilter
      // Remove searchTerm from API params to prevent server-side filtering issues
      // if (searchTerm) params.search = searchTerm

      const response = await leaveService.getAllLeaves(params)
      
      if (response.success) {
        setLeaves(response.data?.leaves || [])
      } else {
        setError(response.message || 'Failed to fetch leaves')
      }
    } catch (error) {
      console.error('Error fetching leaves:', error)
      setError('Failed to load leaves')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, branchFilter, leaveTypeFilter]) // Remove searchTerm from dependencies

  const fetchFilterData = useCallback(async () => {
    try {
      const [branchesRes, leaveTypesRes] = await Promise.all([
        branchService.getAllBranches(),
        leaveTypeService.getAllLeaveTypes()
      ])

      if (branchesRes.success) {
        const branchesList = branchesRes.data?.branches || branchesRes.data || []
        setBranches(branchesList.filter(branch => branch.isActive))
      }

      if (leaveTypesRes.success) {
        const leaveTypesList = leaveTypesRes.data?.leaveTypes || leaveTypesRes.data || []
        setLeaveTypes(leaveTypesList.filter(type => type.isActive))
      }
    } catch (error) {
      console.error('Error fetching filter data:', error)
    }
  }, [])

  useEffect(() => {
    fetchLeaves()
    fetchFilterData()
  }, [fetchLeaves, fetchFilterData])

  const handleCreateLeave = async (leaveData) => {
    try {
      const response = await leaveService.createLeave(leaveData)
      
      if (response.success) {
        showToast('Leave created successfully!', 'success')
        fetchLeaves()
        setShowCreateModal(false)
      } else {
        showToast(response.message || 'Failed to create leave', 'error')
      }
    } catch (error) {
      console.error('Error creating leave:', error)
      showToast('Error creating leave', 'error')
    }
  }

  const handleEditLeave = async (leaveId, leaveData) => {
    try {
      const response = await leaveService.updateLeave(leaveId, leaveData)
      
      if (response.success) {
        showToast('Leave updated successfully!', 'success')
        fetchLeaves()
        setShowEditModal(false)
        setSelectedLeave(null)
      } else {
        showToast(response.message || 'Failed to update leave', 'error')
      }
    } catch (error) {
      console.error('Error updating leave:', error)
      showToast('Error updating leave', 'error')
    }
  }

  const handleDeleteLeave = async (leaveId) => {
    try {
      const response = await leaveService.deleteLeave(leaveId)
      
      if (response.success) {
        showToast('Leave deleted successfully!', 'success')
        fetchLeaves()
        setShowDeleteModal(false)
        setSelectedLeave(null)
      } else {
        showToast(response.message || 'Failed to delete leave', 'error')
      }
    } catch (error) {
      console.error('Error deleting leave:', error)
      showToast('Error deleting leave', 'error')
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
    
    switch (status?.toLowerCase()) {
      case 'approved':
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        )
      case 'pending':
      default:
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            <AlertTriangle className="w-3 h-3 mr-1" />
            Pending
          </span>
        )
    }
  }

  const filteredLeaves = leaves.filter(leave => {
    // Search functionality - make it more robust and client-side only
    let matchesSearch = true
    
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim()
      matchesSearch = 
        (leave.employee?.fullName && leave.employee.fullName.toLowerCase().includes(searchLower)) ||
        (leave.employee?.contactNumber && leave.employee.contactNumber.toLowerCase().includes(searchLower)) ||
        (leave.leaveType?.name && leave.leaveType.name.toLowerCase().includes(searchLower)) ||
        (leave.branch?.name && leave.branch.name.toLowerCase().includes(searchLower)) ||
        (leave.status && leave.status.toLowerCase().includes(searchLower)) ||
        (leave.reason && leave.reason.toLowerCase().includes(searchLower))
    }

    const matchesStatus = statusFilter === 'all' || leave.status === statusFilter
    const matchesBranch = branchFilter === 'all' || leave.branch?._id === branchFilter
    const matchesLeaveType = leaveTypeFilter === 'all' || leave.leaveType?._id === leaveTypeFilter

    return matchesSearch && matchesStatus && matchesBranch && matchesLeaveType
  })

  const handleClearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setBranchFilter('all')
    setLeaveTypeFilter('all')
    setDateFilter('all')
  }

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || branchFilter !== 'all' || leaveTypeFilter !== 'all' || dateFilter !== 'all'

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          toastMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <div className="flex items-center">
            {toastMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <XCircle className="w-5 h-5 mr-2" />
            )}
            {toastMessage.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Leave Management</h1>
        </div>
        <div className="flex space-x-3 mt-4 md:mt-0">
          <button
            onClick={() => fetchLeaves()}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Leave
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </h3>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium"
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search employees, leave types, branches..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {statusFilter !== 'all' && (
                <button
                  onClick={() => setStatusFilter('all')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear status filter"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Branch Filter */}
          <div className="relative">
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
            >
              <option value="all">All Branches</option>
              {branches.map(branch => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {branchFilter !== 'all' && (
                <button
                  onClick={() => setBranchFilter('all')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear branch filter"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Leave Type Filter */}
          <div className="relative">
            <select
              value={leaveTypeFilter}
              onChange={(e) => setLeaveTypeFilter(e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
            >
              <option value="all">All Leave Types</option>
              {leaveTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.name}
                </option>
              ))}
            </select>
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              {leaveTypeFilter !== 'all' && (
                <button
                  onClick={() => setLeaveTypeFilter('all')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear leave type filter"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
              <ChevronDown className="w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Leave List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Leave Applications ({filteredLeaves.length})
            </h3>
          </div>
        </div>

        {filteredLeaves.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leaves found</h3>
            <p className="text-gray-600 mb-4">
              {hasActiveFilters 
                ? 'No leaves match your current filters. Try adjusting your search criteria.'
                : 'Get started by creating your first leave application.'
              }
            </p>
            {!hasActiveFilters && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Leave
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Leave Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeaves.map(leave => (
                  <tr key={leave._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-8 h-8 text-gray-400 bg-gray-100 rounded-full p-1.5 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {leave.employee?.fullName || 'Unknown Employee'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {leave.employee?.contactNumber || ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {leave.leaveType?.name || 'Unknown'}
                      </div>
                      {leave.isHalfDay && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          Half Day
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(leave.startDate)}
                      </div>
                      <div className="text-sm text-gray-500">
                        to {formatDate(leave.endDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <MapPin className="w-4 h-4 text-gray-400 mr-1" />
                        {leave.branch?.name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(leave.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(leave.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedLeave(leave)
                            setShowViewModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLeave(leave)
                            setShowEditModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="Edit Leave"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLeave(leave)
                            setShowDeleteModal(true)
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1 rounded"
                          title="Delete Leave"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateLeaveModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateLeave}
      />

      <EditLeaveModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false)
          setSelectedLeave(null)
        }}
        onSubmit={handleEditLeave}
        leave={selectedLeave}
      />

      <ViewLeaveModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false)
          setSelectedLeave(null)
        }}
        leave={selectedLeave}
      />

      <DeleteLeaveModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedLeave(null)
        }}
        onConfirm={handleDeleteLeave}
        leave={selectedLeave}
      />
    </div>
  )
}

export default LeaveApplications
